import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BellSimple, BellSimpleRinging } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { ProductCard } from "@/components/ProductCard";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "art", label: "Art" },
  { key: "cars", label: "Motorcars" },
  { key: "jewellery", label: "Jewellery" },
  { key: "fashion", label: "Couture" },
  { key: "watches", label: "Timepieces" },
];

export default function Marketplace() {
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waitlist, setWaitlist] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/products").then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (user) api.get("/waitlist/mine").then(({ data }) => setWaitlist(data)).catch(() => {});
    else setWaitlist([]);
  }, [user]);

  const subscribed = waitlist.includes(active);
  const toggleWaitlist = async () => {
    if (!user) { setAuthOpen(true); return; }
    try {
      if (subscribed) {
        await api.delete(`/waitlist/${active}`);
        setWaitlist(waitlist.filter((c) => c !== active));
        toast.success("Alerts turned off.");
      } else {
        await api.post("/waitlist", { category: active });
        setWaitlist([...waitlist, active]);
        toast.success(`We'll email you when a new ${active} piece arrives.`);
      }
    } catch {
      toast.error("Something went wrong.");
    }
  };

  const shown = active === "all" ? products : products.filter((p) => p.category === active);

  return (
    <div className="min-h-screen bg-[#050505] px-6 pb-32 pt-32 md:px-12 md:pt-40">
      <div className="mx-auto max-w-[1600px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
        >
          <p className="text-[11px] uppercase tracking-[0.4em] text-gold">The Collection</p>
          <h1 className="font-serif-lux mt-4 text-5xl font-light leading-none text-[#f5f5f0] md:text-8xl">
            Each exists once
          </h1>
        </motion.div>

        <div data-testid="category-filters" className="mt-14 flex flex-wrap items-center gap-3 border-b border-white/10 pb-6">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setParams(f.key === "all" ? {} : { category: f.key })}
              className={`rounded-none border px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors ${
                active === f.key
                  ? "border-gold bg-gold text-black"
                  : "border-white/20 text-[#a1a1aa] hover:border-[#f5f5f0] hover:text-[#f5f5f0]"
              }`}
            >
              {f.label}
            </button>
          ))}
          {active !== "all" && (
            <button
              data-testid="waitlist-toggle"
              onClick={toggleWaitlist}
              className={`ml-auto flex items-center gap-2 rounded-none border px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors ${
                subscribed ? "border-gold text-gold" : "border-white/20 text-[#a1a1aa] hover:border-[#f5f5f0] hover:text-[#f5f5f0]"
              }`}
            >
              {subscribed ? <BellSimpleRinging size={14} weight="fill" /> : <BellSimple size={14} />}
              {subscribed ? "Alerts on" : "Notify me of new arrivals"}
            </button>
          )}
        </div>

        {loading ? (
          <p className="mt-20 text-sm font-light text-[#52525b]">Curating…</p>
        ) : shown.length === 0 ? (
          <p data-testid="empty-state" className="mt-20 font-serif-lux text-3xl font-light italic text-[#52525b]">
            Nothing in this category — for now.
          </p>
        ) : (
          <div className="mt-14 grid grid-cols-1 gap-x-6 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} tall />
            ))}
          </div>
        )}
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="register" />
    </div>
  );
}
